import React, { useEffect, useState, useRef } from 'react';
import { Modal, Tooltip, Input, Button, Select, Tag, Spin, Empty } from 'antd';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import 'antd/dist/antd.css';
import { useHistory } from "react-router-dom";
import {
    ADMIN_GET_ALL_APPROVED_USERS,
    ADMIN_GET_ALL_KYC_PENDING_USERS,
    USER_ROLE,
    USER_BLOCK_UNBLOCK,
    USER_BLOCK_LOG,
    USER_VERIFY_MPIN
} from './../../../Utils/appConstants';
import { getRequest, postRequest } from './../../../Utils/AppApi';
import { ToastContainer, toast } from 'react-toastify';
import { getUser, kycStatusValue } from './../../../Utils/Common';
import UserPermissionModal from './../../../container/UserPermissionModal';

const PRIMARY_RED = '#C9262C';
const PRIMARY_GREEN = '#02A650';
const PRIMARY_AMBER = '#F59E0B';


const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDisplayDate = (value, forceShowTime = false) => {
    if (value === null || value === undefined || value === "") return "—";
    try {
        let d;
        let hasTime = false;

        if (value instanceof Date) {
            d = value;
            hasTime = true;
        } else if (typeof value === "string") {
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                const [y, m, dd] = value.split("-").map(Number);
                d = new Date(y, m - 1, dd);
                hasTime = false;
            } else {
                d = new Date(value);
                hasTime = true;
            }
        } else if (typeof value === "number") {
            d = new Date(value);
            hasTime = true;
        } else {
            return "—";
        }

        if (!d || isNaN(d.getTime())) return "—";

        const day = String(d.getDate()).padStart(2, "0");
        const month = MONTHS[d.getMonth()];
        const year = d.getFullYear();
        const datePart = `${day}-${month}-${year}`;

        if (!hasTime && !forceShowTime) return datePart;

        const hh = String(d.getHours()).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        return `${datePart} ${hh}:${mm}`;
    } catch {
        return "—";
    }
};

const getRowUpdatedAt = (row) => {
    return (
        row?.updatedAt ||
        row?.user?.updatedAt ||
        (Array.isArray(row?.khatabooks) && row.khatabooks[0]?.updatedAt) ||
        row?.createdAt ||
        null
    );
};

// ==================== Component ====================

function AccessManagement() {
    const user = getUser();
    const history = useHistory();

    // ---- Page-level MPIN gate ----
    const [isMpinVerified, setIsMpinVerified] = useState(false);
    const [mpinInput, setMpinInput] = useState("");
    const [mpinLoading, setMpinLoading] = useState(false);
    const [mpinError, setMpinError] = useState("");

    // ---- Action-level MPIN gate ----
    const [isActionMpinModalOpen, setIsActionMpinModalOpen] = useState(false);
    const [actionMpinValue, setActionMpinValue] = useState("");
    const [actionMpinLoading, setActionMpinLoading] = useState(false);
    const [actionMpinError, setActionMpinError] = useState("");
    const [pendingActionCallback, setPendingActionCallback] = useState(null);
    const [pendingActionName, setPendingActionName] = useState("");

    // ---- Filters & paging ----
    const [currentType, setCurrentType] = useState("all");
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [roleId, setRoleId] = useState("all");
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);

    const [allItems, setAllItems] = useState([]);
    const [displayItems, setDisplayItems] = useState([]);
    const [perPage, setPerPage] = useState(10);

    const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, blocked: 0 });

    const [isModalUserPermission, setIsModalUserPermission] = useState(false);
    const [permissionUser, setPermissionUser] = useState(null);

    const [isModalBlockUnblock, setIsModalBlockUnblock] = useState(false);
    const [blockUnblockUser, setBlockUnblockUser] = useState(null);
    const [blockUnblockStatus, setBlockUnblockStatus] = useState(null);
    const [blockUnblockRemark, setBlockUnblockRemark] = useState("");
    const [blockUnblockSaving, setBlockUnblockSaving] = useState(false);

    const [isModalBlockLog, setIsModalBlockLog] = useState(false);
    const [blockLogData, setBlockLogData] = useState([]);
    const [blockLogLoading, setBlockLogLoading] = useState(false);

    const [getCsvData, setCsvData] = useState([]);
    const csvLink = useRef();

    // ================== PAGE-LEVEL MPIN ==================

    const handleVerifyMpin = () => {
        const mpin = mpinInput.trim();

        if (mpin.length === 0) {
            setMpinError("Please enter your M-PIN");
            return;
        }
        if (mpin.length !== 6) {
            setMpinError("M-PIN must be 6 digits");
            return;
        }
        if (!/^\d+$/.test(mpin)) {
            setMpinError("M-PIN must contain only digits");
            return;
        }

        setMpinLoading(true);
        setMpinError("");

        postRequest(USER_VERIFY_MPIN, { mPin: mpin }, history)
            .then((response) => {
                if (response.success === true) {
                    setIsMpinVerified(true);
                    setMpinInput("");
                    toast.success("Verified");
                } else {
                    setMpinError(response.message || "Invalid M-PIN");
                }
            })
            .catch(() => setMpinError("Verification failed. Please try again."))
            .finally(() => setMpinLoading(false));
    };

    const handleMpinKeyDown = (e) => {
        if (e.key === "Enter") {
            handleVerifyMpin();
        }
    };

    // ================== ACTION-LEVEL MPIN ==================

    /**
     * Opens the MPIN modal and remembers what action to run on success.
     * Nothing executes until MPIN is verified.
     */
    const requireMpin = (actionName, actionCallback) => {
        setPendingActionName(actionName);
        setPendingActionCallback(() => actionCallback);
        setActionMpinValue("");
        setActionMpinError("");
        setIsActionMpinModalOpen(true);
    };

    const closeActionMpinModal = () => {
        setIsActionMpinModalOpen(false);
        setPendingActionCallback(null);
        setActionMpinValue("");
        setActionMpinError("");
        setPendingActionName("");
    };

    const handleActionMpinVerify = () => {
        const mpin = actionMpinValue.trim();

        if (mpin.length === 0) {
            setActionMpinError("Please enter your M-PIN");
            return;
        }
        if (mpin.length !== 6) {
            setActionMpinError("M-PIN must be 6 digits");
            return;
        }
        if (!/^\d+$/.test(mpin)) {
            setActionMpinError("M-PIN must contain only digits");
            return;
        }

        setActionMpinLoading(true);
        setActionMpinError("");

        postRequest(USER_VERIFY_MPIN, { mPin: mpin }, history)
            .then((response) => {
                if (response.success === true) {
                    const cb = pendingActionCallback;
                    closeActionMpinModal();
                    toast.success("Verified");
                    if (typeof cb === "function") cb();
                } else {
                    setActionMpinError(response.message || "Invalid M-PIN");
                }
            })
            .catch(() => setActionMpinError("Verification failed. Please try again."))
            .finally(() => setActionMpinLoading(false));
    };

    const handleActionMpinKeyDown = (e) => {
        if (e.key === "Enter") {
            handleActionMpinVerify();
        }
    };

    // ================== FETCH ==================

    const objectToQueryString = (obj) => {
        const str = [];
        for (const p in obj)
            if (obj.hasOwnProperty(p) && obj[p] !== "" && obj[p] !== null && obj[p] !== undefined) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        return str.join("&");
    };

    const fetchEndpoint = (endpoint, source) => {
        return new Promise((resolve) => {
            const params = {
                page: 1,
                limit: 200,
                roleId: roleId || "all"
            };

            const qs = objectToQueryString(params);
            getRequest(`${endpoint}?${qs}`, history)
                .then((res) => {
                    if (res.success === true && res.data && Array.isArray(res.data.docs)) {
                        resolve(res.data.docs.map((d) => ({ ...d, __source: source })));
                    } else {
                        resolve([]);
                    }
                })
                .catch(() => resolve([]));
        });
    };

    const fetchData = () => {
        setLoading(true);

        const tasks = [];
        if (currentType === "all" || currentType === "approved") {
            tasks.push(fetchEndpoint(ADMIN_GET_ALL_APPROVED_USERS, "approved"));
        }
        if (currentType === "all" || currentType === "pending") {
            tasks.push(fetchEndpoint(ADMIN_GET_ALL_KYC_PENDING_USERS, "pending"));
        }

        Promise.all(tasks)
            .then((results) => {
                const merged = results.flat();

                const seen = new Set();
                const unique = [];
                merged.forEach((item) => {
                    const id = item.userId || item._id;
                    if (!id || seen.has(id)) return;
                    seen.add(id);
                    unique.push(item);
                });

                setAllItems(unique);
                computeStats(unique);
                applyFilters(unique);
            })
            .catch((err) => {
                console.log(err);
                setAllItems([]);
                setDisplayItems([]);
            })
            .finally(() => setLoading(false));
    };

    const computeStats = (items) => {
        let approved = 0, pending = 0, blocked = 0;
        items.forEach((it) => {
            if (it.__source === "approved") approved++;
            else pending++;
            const isActive = it.user?.status === undefined || it.user?.status === null || it.user?.status === true;
            if (!isActive) blocked++;
        });
        setStats({ total: items.length, approved, pending, blocked });
    };

    const applyFilters = (items) => {
        let arr = [...items];

        if (currentType === "approved") {
            arr = arr.filter((i) => i.__source === "approved");
        } else if (currentType === "pending") {
            arr = arr.filter((i) => i.__source === "pending");
        }

        if (search) {
            const q = search.toLowerCase();
            arr = arr.filter((item) => {
                const username = (item.user?.username || "").toLowerCase();
                const name = (item.user?.name || "").toLowerCase();
                const mobile = (item.mobile || "").toLowerCase();
                const parentUser = (item.userParentDetail?.username || "").toLowerCase();
                return username.includes(q) || name.includes(q) || mobile.includes(q) || parentUser.includes(q);
            });
        }

        setDisplayItems(arr);
    };

    const handleSearch = () => setSearch(searchInput.trim());

    const handleClear = () => {
        setCurrentType("all");
        setSearchInput("");
        setSearch("");
        setRoleId("all");
    };

    const setType = (type) => {
        if (type === currentType) return;
        setCurrentType(type);
    };

    useEffect(() => {
        if (!isMpinVerified) return;
        fetchData();
    }, [isMpinVerified, currentType, roleId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isMpinVerified) return;
        applyFilters(allItems);
    }, [search, currentType, isMpinVerified]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isMpinVerified) return;
        getRequest(USER_ROLE, history)
            .then((response) => {
                if (response.success === true) setRoles(response.data || []);
            })
            .catch(() => { });
    }, [isMpinVerified]); // eslint-disable-line react-hooks/exhaustive-deps

    // ================== HANDLERS (each wrapped with MPIN) ==================

    const handleOpenBlockUnblock = (row) => {
        const isActive = row.user?.status === undefined || row.user?.status === null || row.user?.status === true;
        const actionName = isActive ? "Block User" : "Unblock User";

        requireMpin(actionName, () => {
            setBlockUnblockUser({
                userId: row.userId,
                username: row.user?.username || '',
                name: row.user?.name || ''
            });
            setBlockUnblockStatus(!isActive);
            setBlockUnblockRemark("");
            setIsModalBlockUnblock(true);
        });
    };

    const handleSubmitBlockUnblock = () => {
        if (!blockUnblockUser) return;
        if (!blockUnblockRemark.trim()) {
            toast.error("Remark is required");
            return;
        }

        setBlockUnblockSaving(true);
        const payload = {
            userId: blockUnblockUser.userId,
            status: blockUnblockStatus,
            remark: blockUnblockRemark.trim()
        };

        postRequest(`${USER_BLOCK_UNBLOCK}`, payload, history)
            .then((response) => {
                if (response.success === true) {
                    toast.success(response.message);
                    setIsModalBlockUnblock(false);
                    setBlockUnblockUser(null);
                    setBlockUnblockRemark("");

                    const updated = allItems.map((it) => {
                        if (it.userId === payload.userId && it.user) {
                            return { ...it, user: { ...it.user, status: payload.status } };
                        }
                        return it;
                    });
                    setAllItems(updated);
                    computeStats(updated);
                    applyFilters(updated);
                } else {
                    toast.error(response.message);
                }
            })
            .catch(() => toast.error("Failed to update user status"))
            .finally(() => setBlockUnblockSaving(false));
    };

    const handleOpenBlockLog = (row) => {
        requireMpin("View Block Log", () => {
            setBlockUnblockUser({
                userId: row.userId,
                username: row.user?.username || '',
                name: row.user?.name || ''
            });
            setBlockLogData([]);
            setIsModalBlockLog(true);
            setBlockLogLoading(true);

            getRequest(`${USER_BLOCK_LOG}?userId=${row.userId}&page=1&limit=50`, history)
                .then((response) => {
                    if (response.success === true && response.data) {
                        setBlockLogData(response.data.docs || []);
                    }
                })
                .catch(() => { })
                .finally(() => setBlockLogLoading(false));
        });
    };

    const handleOpenPermissionModal = (row) => {
        requireMpin("Manage User Permissions", () => {
            setPermissionUser({
                userId: row.userId,
                username: row.user?.username || '',
                name: row.user?.name || ''
            });
            setIsModalUserPermission(true);
        });
    };

    const handleClosePermissionModal = () => {
        setIsModalUserPermission(false);
        setPermissionUser(null);
    };

    const handleExportCsv = () => {
        const rows = displayItems.map((e) => ({
            "Action": e.user?.status === false ? "Blocked" : "Active",
            "User Id": e.user?.username || "",
            "Name": e.user?.name || "",
            "User Type": e.role?.name || "",
            "Mobile": e.mobile || "",
            "Outlet": e.outletName || "",
            "KYC Status": kycStatusValue(e.user?.KYCStatus),
            "Parent Id": e.userParentDetail?.username || "",
            "Parent Name": e.userParentDetail?.name || "",
            "Source": e.__source === "approved" ? "Approved" : "KYC Pending",
            "Onboard Date": formatDisplayDate(e.createdAt),
            "Updated At": formatDisplayDate(getRowUpdatedAt(e))
        }));

        setCsvData(rows);
        setTimeout(() => {
            if (csvLink.current) csvLink.current.link.click();
        }, 500);
    };

    // ================== COLUMNS ==================

    const columns = [
        {
            name: 'Actions',
            cell: (row) => {
                const isActive = row.user?.status === undefined || row.user?.status === null || row.user?.status === true;
                return (
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        <Tooltip title={isActive ? "Block user" : "Unblock user"}>
                            <button
                                className="action-btn"
                                style={{
                                    background: isActive ? '#FEE2E2' : '#DCFCE7',
                                    color: isActive ? PRIMARY_RED : PRIMARY_GREEN,
                                    border: `1px solid ${isActive ? '#FCA5A5' : '#86EFAC'}`
                                }}
                                onClick={() => handleOpenBlockUnblock(row)}
                            >
                                <i className={`fa ${isActive ? 'fa-lock' : 'fa-unlock'}`}></i>
                            </button>
                        </Tooltip>

                        <Tooltip title="Permissions">
                            <button
                                className="action-btn"
                                style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FCD34D' }}
                                onClick={() => handleOpenPermissionModal(row)}
                            >
                                <i className="fa fa-user-shield"></i>
                            </button>
                        </Tooltip>
                    </div>
                );
            },
            width: "140px"
        },
        {
            name: 'User',
            width: "150px",
            cell: (row) => (
                <div>
                    <div style={{ fontWeight: 600, color: '#1A1A2E', fontSize: 13 }}>
                        {row.user?.name || "—"}
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7280' }}>
                        <i className="fa fa-id-badge mr-1"></i>
                        {row.user?.username || "—"}
                    </div>
                </div>
            )
        },
        {
            name: 'Type',
            width: "150px",
            cell: (row) => (
                <span style={{
                    fontSize: 11, fontWeight: 600,
                    color: '#374151',
                    background: '#F3F4F6',
                    padding: '3px 8px',
                    borderRadius: 6
                }}>
                    {row.role?.name || "—"}
                </span>
            )
        },
        {
            name: 'Mobile',
            selector: (row) => row.mobile || "—",
            width: "120px"
        },
        {
            name: 'Outlet',
            selector: (row) => row.outletName || "—",
            width: "200px"
        },
        {
            name: 'KYC',
            width: "150px",
            cell: (row) => {
                const kyc = row.user?.KYCStatus;
                const map = {
                    0: { label: 'Pending', color: '#6B7280', bg: '#F3F4F6' },
                    1: { label: 'In Process', color: '#B45309', bg: '#FEF3C7' },
                    2: { label: 'Rejected', color: PRIMARY_RED, bg: '#FEE2E2' },
                    3: { label: 'Approved', color: PRIMARY_GREEN, bg: '#DCFCE7' }
                };
                const s = map[kyc] || map[0];
                return (
                    <span style={{
                        fontSize: 11, fontWeight: 600,
                        color: s.color, background: s.bg,
                        padding: '3px 8px', borderRadius: 6
                    }}>
                        {s.label}
                    </span>
                );
            }
        },
        {
            name: 'Access',
            width: "100px",
            cell: (row) => {
                const isActive = row.user?.status === undefined || row.user?.status === null || row.user?.status === true;
                return (
                    <span style={{
                        fontSize: 11, fontWeight: 700,
                        color: isActive ? PRIMARY_GREEN : PRIMARY_RED,
                        background: isActive ? '#DCFCE7' : '#FEE2E2',
                        padding: '3px 8px', borderRadius: 6
                    }}>
                        {isActive ? 'Active' : 'Blocked'}
                    </span>
                );
            }
        },
        {
            name: 'Parent',
            width: "150px",
            cell: (row) => (
                <div>
                    <div style={{ fontSize: 12, color: '#1A1A2E' }}>
                        {row.userParentDetail?.name || "—"}
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7280' }}>
                        {row.userParentDetail?.username || ""}
                    </div>
                </div>
            )
        },
        {
            name: 'Onboard Date',
            width: "150px",
            cell: (row) => (
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                    {formatDisplayDate(row.createdAt)}
                </span>
            )
        },
        {
            name: 'Updated At',
            width: "150px",
            cell: (row) => (
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                    {formatDisplayDate(getRowUpdatedAt(row))}
                </span>
            )
        }
    ];

    // ================== PAGE-LEVEL MPIN GATE ==================

    if (!isMpinVerified) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #F9FAFB 0%, #FEF2F2 100%)',
                padding: 20
            }}>
                <div style={{
                    background: '#fff',
                    borderRadius: 16,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)',
                    border: '1px solid #F3F4F6',
                    width: '100%',
                    maxWidth: 440,
                    overflow: 'hidden'
                }}>
                    <div style={{
                        background: PRIMARY_RED,
                        padding: '28px 24px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            width: 64, height: 64, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 12px'
                        }}>
                            <i className="fa fa-lock" style={{ fontSize: 28, color: '#fff' }}></i>
                        </div>
                        <div style={{ color: '#fff', fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
                            Access Management
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>
                            This area is protected. Enter your 6-digit M-PIN to continue.
                        </div>
                    </div>

                    <div style={{ padding: '28px 28px 24px' }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                            Enter M-PIN
                        </label>

                        <Input.Password
                            size="large"
                            placeholder="••••••"
                            value={mpinInput}
                            onChange={(e) => {
                                const raw = e.target.value.replace(/\D/g, '');
                                setMpinInput(raw.slice(0, 6));
                                if (mpinError) setMpinError("");
                            }}
                            onPressEnter={handleMpinKeyDown}
                            maxLength={6}
                            autoFocus
                            status={mpinError ? "error" : ""}
                            style={{ fontSize: 18, letterSpacing: 8, textAlign: 'center', height: 48, borderRadius: 10 }}
                        />

                        {mpinError && (
                            <div style={{ fontSize: 12, color: PRIMARY_RED, marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <i className="fa fa-exclamation-circle"></i>
                                {mpinError}
                            </div>
                        )}

                        <button
                            onClick={handleVerifyMpin}
                            disabled={mpinLoading || mpinInput.length !== 6}
                            style={{
                                width: '100%',
                                height: 48,
                                marginTop: 20,
                                background: mpinInput.length === 6 ? PRIMARY_RED : '#E5E7EB',
                                color: mpinInput.length === 6 ? '#fff' : '#9CA3AF',
                                border: 'none',
                                borderRadius: 10,
                                fontSize: 15,
                                fontWeight: 700,
                                cursor: mpinInput.length === 6 && !mpinLoading ? 'pointer' : 'not-allowed',
                                transition: 'all 0.15s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8
                            }}
                        >
                            {mpinLoading ? (
                                <><Spin size="small" /> Verifying…</>
                            ) : (
                                <><i className="fa fa-shield"></i> Verify & Continue</>
                            )}
                        </button>

                        <button
                            onClick={() => history.goBack()}
                            style={{
                                width: '100%',
                                height: 42,
                                marginTop: 10,
                                background: 'transparent',
                                color: '#6B7280',
                                border: '1px solid #E5E7EB',
                                borderRadius: 10,
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            Cancel
                        </button>

                        <div style={{ textAlign: 'center', fontSize: 11, color: '#9CA3AF', marginTop: 18, lineHeight: 1.5 }}>
                            <i className="fa fa-info-circle mr-1"></i>
                            M-PIN is the same PIN you use to log in
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ================== MAIN UI ==================

    return (
        <div>
            <style>{`
                .action-btn {
                    width: 30px;
                    height: 30px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.15s;
                    font-size: 12px;
                }
                .action-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 2px 6px rgba(0,0,0,0.12);
                }
                .chip-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    height: 40px;
                    padding: 0 18px;
                    border-radius: 10px;
                    border: 1px solid #E5E7EB;
                    background: #fff;
                    color: #374151;
                    font-weight: 600;
                    font-size: 13px;
                    cursor: pointer;
                    transition: all 0.15s;
                }
                .chip-btn:hover {
                    border-color: #9CA3AF;
                    background: #FAFAFA;
                }
                .chip-btn.active {
                    color: #fff;
                    border-color: transparent;
                }
                .stat-card-row {
                    display: flex;
                    gap: 12px;
                    margin-bottom: 16px;
                }
            `}</style>

            <div className="container-fluid pt-2 pb-5">
                <div className="content-header my-0 py-0">
                    <div className="container-fluid mb-3 ml-2">
                        <div className="row mb-0 align-items-center">
                            <div className="col-sm-8">
                                <h1 className='page-title text-blue font-merriweather'>
                                    User Access <span className='text-red'>Management</span>
                                </h1>
                                <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
                                    Manage access, permissions, and block status for all users in one place
                                </div>
                            </div>
                            <div className="col-sm-4 text-right">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleExportCsv}
                                    style={{
                                        background: PRIMARY_RED,
                                        borderColor: PRIMARY_RED,
                                        borderRadius: 10,
                                        fontWeight: 600,
                                        padding: '8px 18px'
                                    }}
                                >
                                    <i className="fa fa-file-excel mr-2"></i> Export CSV
                                </button>
                                <CSVLink
                                    data={getCsvData}
                                    filename='access-management.csv'
                                    className='hidden'
                                    ref={csvLink}
                                    target='_blank'
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-12">
                    <div style={{
                        background: '#fff',
                        borderRadius: 14,
                        padding: 18,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                        border: '1px solid #F3F4F6',
                        marginBottom: 16
                    }}>
                        <div className="d-flex mb-3" style={{ gap: 10, flexWrap: 'wrap' }}>
                            <button
                                className={`chip-btn ${currentType === 'all' ? 'active' : ''}`}
                                style={currentType === 'all' ? { background: PRIMARY_RED } : {}}
                                onClick={() => setType("all")}
                            >
                                <i className="fa fa-th-large"></i> All Users
                                <span style={{
                                    fontSize: 11,
                                    background: currentType === 'all' ? 'rgba(255,255,255,0.25)' : '#F3F4F6',
                                    padding: '2px 8px',
                                    borderRadius: 10,
                                    marginLeft: 4
                                }}>
                                    {stats.total}
                                </span>
                            </button>
                            <button
                                className={`chip-btn ${currentType === 'approved' ? 'active' : ''}`}
                                style={currentType === 'approved' ? { background: PRIMARY_GREEN } : {}}
                                onClick={() => setType("approved")}
                            >
                                <i className="fa fa-check-circle"></i> Approved
                                <span style={{
                                    fontSize: 11,
                                    background: currentType === 'approved' ? 'rgba(255,255,255,0.25)' : '#F3F4F6',
                                    padding: '2px 8px',
                                    borderRadius: 10,
                                    marginLeft: 4
                                }}>
                                    {stats.approved}
                                </span>
                            </button>
                            <button
                                className={`chip-btn ${currentType === 'pending' ? 'active' : ''}`}
                                style={currentType === 'pending' ? { background: PRIMARY_AMBER } : {}}
                                onClick={() => setType("pending")}
                            >
                                <i className="fa fa-clock-o"></i> KYC Pending
                                <span style={{
                                    fontSize: 11,
                                    background: currentType === 'pending' ? 'rgba(255,255,255,0.25)' : '#F3F4F6',
                                    padding: '2px 8px',
                                    borderRadius: 10,
                                    marginLeft: 4
                                }}>
                                    {stats.pending}
                                </span>
                            </button>
                        </div>

                        <div className="row">
                            <div className="col-md-3">
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                                    User Type
                                </label>
                                <Select
                                    className="w-100"
                                    placeholder="All"
                                    value={roleId}
                                    onChange={(v) => setRoleId(v || "all")}
                                    allowClear
                                    onClear={() => setRoleId("all")}
                                    size="large"
                                    options={[
                                        { value: "all", label: "All Roles" },
                                        ...roles.map((r) => ({ value: r._id, label: r.name }))
                                    ]}
                                />
                            </div>
                            <div className="col-md-6">
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                                    Search
                                </label>
                                <Input
                                    size="large"
                                    placeholder="Search by user id / name / mobile / parent"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onPressEnter={handleSearch}
                                    allowClear
                                    prefix={<i className="fa fa-search" style={{ color: '#9CA3AF' }}></i>}
                                />
                            </div>
                            <div className="col-md-3">
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                                    &nbsp;
                                </label>
                                <div className="d-flex" style={{ gap: 8 }}>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSearch}
                                        style={{
                                            flex: 1,
                                            background: PRIMARY_RED,
                                            borderColor: PRIMARY_RED,
                                            borderRadius: 10,
                                            height: 40,
                                            fontWeight: 600
                                        }}
                                    >
                                        <i className="fa fa-search mr-1"></i> Search
                                    </button>
                                    <button
                                        className="btn btn-default"
                                        onClick={handleClear}
                                        style={{ borderRadius: 10, height: 40, fontWeight: 600 }}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        background: '#fff',
                        borderRadius: 14,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                        border: '1px solid #F3F4F6',
                        overflow: 'hidden'
                    }}>
                        <DataTable
                            columns={columns}
                            data={displayItems}
                            progressPending={loading}
                            progressComponent={
                                <div style={{ padding: '60px 0', textAlign: 'center' }}>
                                    <Spin size="large" />
                                    <div style={{ marginTop: 12, color: '#6B7280', fontSize: 13 }}>
                                        Loading users…
                                    </div>
                                </div>
                            }
                            highlightOnHover
                            pagination
                            paginationPerPage={perPage}
                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                            onChangeRowsPerPage={(n) => setPerPage(n)}
                            noDataComponent={
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <div style={{ color: '#6B7280' }}>
                                            <div style={{ fontWeight: 600 }}>No users found</div>
                                            <div style={{ fontSize: 12 }}>Try adjusting your filters or search</div>
                                        </div>
                                    }
                                    style={{ padding: '50px 0' }}
                                />
                            }
                            customStyles={{
                                headRow: {
                                    style: {
                                        background: '#F9FAFB',
                                        minHeight: 44,
                                        borderBottom: '1px solid #E5E7EB'
                                    }
                                },
                                headCells: {
                                    style: {
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: '#374151',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.4
                                    }
                                },
                                rows: {
                                    style: {
                                        minHeight: 60,
                                        fontSize: 13
                                    }
                                },
                                cells: {
                                    style: {
                                        paddingTop: 8,
                                        paddingBottom: 8
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            </div>

            <ToastContainer
                position="top-center"
                autoClose={5000}
                limit={2}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />

            {/* ============ ACTION-LEVEL MPIN MODAL ============ */}
            <Modal
                open={isActionMpinModalOpen}
                onCancel={closeActionMpinModal}
                onOk={handleActionMpinVerify}
                okText={actionMpinLoading ? "Verifying…" : "Verify"}
                cancelText="Cancel"
                confirmLoading={actionMpinLoading}
                width={440}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 8,
                            background: '#FEE2E2',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: PRIMARY_RED, fontSize: 16
                        }}>
                            <i className="fa fa-shield"></i>
                        </div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1A2E' }}>
                                Verify M-PIN
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 400, color: '#6B7280', marginTop: 2 }}>
                                {pendingActionName || "Continue with this action"}
                            </div>
                        </div>
                    </div>
                }
                okButtonProps={{
                    disabled: actionMpinValue.length !== 6 || actionMpinLoading,
                    style: {
                        background: actionMpinValue.length === 6 ? PRIMARY_RED : '#E5E7EB',
                        borderColor: actionMpinValue.length === 6 ? PRIMARY_RED : '#E5E7EB',
                        color: actionMpinValue.length === 6 ? '#fff' : '#9CA3AF',
                        fontWeight: 600,
                        borderRadius: 8,
                        height: 40
                    }
                }}
                cancelButtonProps={{
                    style: { borderRadius: 8, height: 40 }
                }}
            >
                <div style={{ paddingTop: 8 }}>
                    <div style={{
                        background: '#F9FAFB',
                        border: '1px solid #F3F4F6',
                        borderRadius: 10,
                        padding: '12px 16px',
                        marginBottom: 16
                    }}>
                        <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                            Action
                        </div>
                        <div style={{ fontSize: 14, color: '#1A1A2E', fontWeight: 600 }}>
                            {pendingActionName || "—"}
                        </div>
                    </div>

                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                        Enter your 6-digit M-PIN
                    </label>

                    <Input.Password
                        size="large"
                        placeholder="••••••"
                        value={actionMpinValue}
                        onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, '');
                            setActionMpinValue(raw.slice(0, 6));
                            if (actionMpinError) setActionMpinError("");
                        }}
                        onPressEnter={handleActionMpinKeyDown}
                        maxLength={6}
                        autoFocus
                        status={actionMpinError ? "error" : ""}
                        style={{
                            fontSize: 18,
                            letterSpacing: 8,
                            textAlign: 'center',
                            height: 48,
                            borderRadius: 10
                        }}
                    />

                    {actionMpinError && (
                        <div style={{
                            fontSize: 12,
                            color: PRIMARY_RED,
                            marginTop: 8,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                        }}>
                            <i className="fa fa-exclamation-circle"></i>
                            {actionMpinError}
                        </div>
                    )}

                    <div style={{
                        fontSize: 11,
                        color: '#9CA3AF',
                        marginTop: 14,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                    }}>
                        <i className="fa fa-info-circle"></i>
                        M-PIN is required for every action in this page.
                    </div>
                </div>
            </Modal>

            {/* ============ BLOCK / UNBLOCK MODAL ============ */}
            {isModalBlockUnblock && blockUnblockUser && (
                <Modal
                    title={
                        <span>
                            <i className={`fa ${blockUnblockStatus ? 'fa-unlock' : 'fa-lock'} mr-2`} />
                            {blockUnblockStatus ? 'Unblock User' : 'Block User'}
                            {' — '}{blockUnblockUser.name} ({blockUnblockUser.username})
                        </span>
                    }
                    width={520}
                    open={isModalBlockUnblock}
                    onOk={handleSubmitBlockUnblock}
                    onCancel={() => {
                        setIsModalBlockUnblock(false);
                        setBlockUnblockUser(null);
                        setBlockUnblockRemark("");
                    }}
                    okText={blockUnblockStatus ? 'Unblock' : 'Block'}
                    cancelText="Cancel"
                    confirmLoading={blockUnblockSaving}
                    okButtonProps={{
                        style: {
                            background: blockUnblockStatus ? PRIMARY_GREEN : PRIMARY_RED,
                            borderColor: blockUnblockStatus ? PRIMARY_GREEN : PRIMARY_RED,
                            fontWeight: 600
                        }
                    }}
                >
                    <div className="row">
                        <div className="col-md-12 mb-2">
                            <small className="text-muted">
                                Current status: <b>{blockUnblockStatus ? 'Blocked' : 'Active'}</b>
                                {' → '}
                                Will become: <b>{blockUnblockStatus ? 'Active' : 'Blocked'}</b>
                            </small>
                        </div>
                        <div className="col-md-12 form-group">
                            <label>Remark <span className="text-danger">*</span></label>
                            <textarea
                                className="form-control"
                                rows={3}
                                placeholder="Reason for this action (required)"
                                value={blockUnblockRemark}
                                onChange={(e) => setBlockUnblockRemark(e.target.value)}
                                style={{ borderRadius: 8, resize: 'none' }}
                            />
                            <small className="text-muted">
                                This remark will be stored with the action and visible in the log.
                            </small>
                        </div>
                    </div>
                </Modal>
            )}

            {/* ============ LOG MODAL ============ */}
            {isModalBlockLog && blockUnblockUser && (
                <Modal
                    title={
                        <span>
                            <i className="fa fa-history mr-2 text-info" />
                            Block / Unblock Log — {blockUnblockUser.name} ({blockUnblockUser.username})
                        </span>
                    }
                    width={720}
                    open={isModalBlockLog}
                    onCancel={() => {
                        setIsModalBlockLog(false);
                        setBlockUnblockUser(null);
                        setBlockLogData([]);
                    }}
                    footer={[
                        <button
                            key="close"
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                setIsModalBlockLog(false);
                                setBlockUnblockUser(null);
                                setBlockLogData([]);
                            }}
                        >
                            Close
                        </button>
                    ]}
                >
                    {blockLogLoading ? (
                        <div className="text-center py-4"><Spin /></div>
                    ) : blockLogData.length === 0 ? (
                        <div className="text-center text-muted py-4">No log entries found.</div>
                    ) : (
                        <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                            <table className="table table-sm table-bordered">
                                <thead>
                                    <tr>
                                        <th style={{ width: 60 }}>#</th>
                                        <th style={{ width: 110 }}>Action</th>
                                        <th style={{ width: 150 }}>Date</th>
                                        <th style={{ width: 150 }}>By</th>
                                        <th>Remark</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {blockLogData.map((log, i) => (
                                        <tr key={log._id || i}>
                                            <td>{i + 1}</td>
                                            <td>
                                                {log.action === 'block'
                                                    ? <span className="badge badge-danger">Blocked</span>
                                                    : <span className="badge badge-success">Unblocked</span>}
                                            </td>
                                            <td>{formatDisplayDate(log.createdAt)}</td>
                                            <td>{log.actionByName || '—'}</td>
                                            <td>{log.remark}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal>
            )}

            <UserPermissionModal
                open={isModalUserPermission}
                user={permissionUser}
                onClose={handleClosePermissionModal}
                theme={{ primary: PRIMARY_RED }}
            />
        </div>
    );
}

export default AccessManagement;